<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import TableHeaderCell from 'dashboard/components/widgets/TableHeaderCell.vue';
import {
  hasPushPermissions,
  requestPushPermissions,
  removePushSubscription,
  verifyServiceWorkerExistence,
} from 'dashboard/helper/pushHelper.js';
import FormSwitch from 'dashboard/components-next/switch/Switch.vue';
import CheckBox from 'v3/components/Form/CheckBox.vue';
import { NOTIFICATION_TYPES } from '../constants';

export default {
  components: {
    TableHeaderCell,
    FormSwitch,
    CheckBox,
  },
  data() {
    return {
      selectedFlags: {
        email: [],
        push: [],
        whatsapp: [],
      },
      enableAudioAlerts: false,
      hasEnabledPushPermissions: false,
      notificationTypes: NOTIFICATION_TYPES,
      defaultNotificationsEnabled: {
        email: false,
        push: false,
        whatsapp: false,
      },
      defaultNotifications: {
        email: [
          'email_conversation_assignment',
          'email_assigned_conversation_new_message',
          'email_conversation_mention',
          'email_lead_distribution',
        ],
        push: [
          'push_conversation_assignment',
          'push_assigned_conversation_new_message',
          'push_conversation_mention',
          'push_lead_distribution',
        ],
        whatsapp: ['whatsapp_lead_distribution'],
      },
    };
  },
  computed: {
    ...mapGetters({
      emailFlags: 'userNotificationSettings/getSelectedEmailFlags',
      pushFlags: 'userNotificationSettings/getSelectedPushFlags',
      whatsappFlags: 'userNotificationSettings/getSelectedWhatsappFlags',
      getAccount: 'accounts/getAccount',
      accountId: 'getCurrentAccountId',
    }),
    whatsappNotificationsEnabled() {
      return (
        this.getAccount(this.accountId)?.whatsapp_notifications_enabled || false
      );
    },
    filteredNotificationTypes() {
      const baseTypes = ['email', 'push'];
      return this.whatsappNotificationsEnabled
        ? [...baseTypes, 'whatsapp']
        : baseTypes;
    },
  },
  watch: {
    emailFlags(value) {
      this.selectedFlags.email = value;
      this.defaultNotificationsEnabled.email =
        value.includes(this.defaultNotifications.email[0]) &&
        value.includes(this.defaultNotifications.email[1]) &&
        value.includes(this.defaultNotifications.email[2]) &&
        value.includes(this.defaultNotifications.email[3]);
    },
    pushFlags(value) {
      this.selectedFlags.push = value;
      this.defaultNotificationsEnabled.push =
        value.includes(this.defaultNotifications.push[0]) &&
        value.includes(this.defaultNotifications.push[1]) &&
        value.includes(this.defaultNotifications.push[2]) &&
        value.includes(this.defaultNotifications.push[3]);
    },
    whatsappFlags(value) {
      this.selectedFlags.whatsapp = value;
      this.defaultNotificationsEnabled.whatsapp = value.includes(
        this.defaultNotifications.whatsapp[0]
      );
    },
  },
  mounted() {
    if (hasPushPermissions()) {
      this.getPushSubscription();
    }
    this.$store.dispatch('userNotificationSettings/get');
  },
  methods: {
    onRegistrationSuccess() {
      this.hasEnabledPushPermissions = true;
    },
    onTogglePushPermissions(shouldEnable) {
      if (shouldEnable) {
        this.onRequestPermissions();
      } else {
        this.onRemovePermissions();
      }
    },
    onRequestPermissions() {
      requestPushPermissions({
        onSuccess: this.onRegistrationSuccess,
        onError: reason => {
          // Keep the switch in its real (off) state when we could not subscribe.
          this.hasEnabledPushPermissions = false;
          if (reason === 'denied') {
            useAlert(
              this.$t(
                'PROFILE_SETTINGS.FORM.NOTIFICATIONS.BROWSER_PERMISSION_BLOCKED'
              )
            );
          }
        },
      });
    },
    onRemovePermissions() {
      removePushSubscription(() => {
        this.hasEnabledPushPermissions = false;
      });
    },
    getPushSubscription() {
      verifyServiceWorkerExistence(registration =>
        registration.pushManager
          .getSubscription()
          .then(subscription => {
            if (!subscription) {
              this.hasEnabledPushPermissions = false;
            } else {
              this.hasEnabledPushPermissions = true;
            }
          })
          // eslint-disable-next-line no-console
          .catch(error => console.log(error))
      );
    },
    async updateNotificationSettings() {
      try {
        this.$store.dispatch('userNotificationSettings/update', {
          selectedEmailFlags: this.selectedFlags.email,
          selectedPushFlags: this.selectedFlags.push,
          selectedWhatsappFlags: this.selectedFlags.whatsapp,
        });
        useAlert(this.$t('PROFILE_SETTINGS.FORM.API.UPDATE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.API.UPDATE_ERROR'));
      }
    },
    toggleDefaultNotifications(type) {
      this.selectedFlags[type] = this.defaultNotificationsEnabled[type]
        ? []
        : this.defaultNotifications[type];
      this.updateNotificationSettings();
    },
  },
};
</script>

<template>
  <div id="profile-settings-notifications" class="flex flex-col gap-6">
    <!-- Layout for desktop devices -->
    <div class="hidden md:block">
      <div
        class="grid content-center h-12 grid-cols-12 gap-4 py-0 rounded-t-xl"
      >
        <TableHeaderCell
          :span="whatsappNotificationsEnabled ? 5 : 7"
          label="`${$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.TYPE_TITLE')}`"
        >
          <span class="text-sm font-normal normal-case text-ash-800">
            {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.TYPE_TITLE') }}
          </span>
        </TableHeaderCell>
        <TableHeaderCell
          :span="2"
          label="`${$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.EMAIL')}`"
        >
          <span class="text-sm font-medium normal-case text-ash-900">
            {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.EMAIL') }}
          </span>
        </TableHeaderCell>
        <TableHeaderCell
          :span="whatsappNotificationsEnabled ? 2 : 3"
          label="`${$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH')}`"
        >
          <div class="flex items-center justify-between gap-1">
            <span
              class="text-sm font-medium normal-case text-ash-900 whitespace-nowrap"
            >
              {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH') }}
            </span>
          </div>
        </TableHeaderCell>
        <TableHeaderCell
          v-if="whatsappNotificationsEnabled"
          :span="3"
          label="WhatsApp"
        >
          <div class="flex items-center justify-between gap-1">
            <span
              class="text-sm font-medium normal-case text-ash-900 whitespace-nowrap"
            >
              {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.WHATSAPP') }}
            </span>
          </div>
        </TableHeaderCell>
      </div>

      <!-- Zimobi Desktop Row -->
      <div>
        <div
          class="grid items-center content-center h-12 grid-cols-12 gap-4 py-0 rounded-t-xl"
        >
          <div
            class="flex flex-row items-start gap-2 px-0 py-2 text-sm tracking-[0.5] rtl:text-right"
            :class="`col-span-${whatsappNotificationsEnabled ? 5 : 7}`"
          >
            <span class="text-sm text-ash-900">
              {{
                $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.SYSTEM_NOTIFICATIONS')
              }}
            </span>
          </div>
          <div
            v-for="(
              notificationType, notificationTypeIndex
            ) in filteredNotificationTypes"
            :key="notificationTypeIndex"
            class="flex items-start gap-2 px-0 text-sm tracking-[0.5] text-left rtl:text-right"
            :class="`col-span-${
              (whatsappNotificationsEnabled && notificationTypeIndex === 2) ||
              (!whatsappNotificationsEnabled && notificationTypeIndex === 1)
                ? 3
                : 2
            }`"
          >
            <CheckBox
              :is-checked="defaultNotificationsEnabled[notificationType]"
              :value="notificationType"
              @update="type => toggleDefaultNotifications(type)"
            />
          </div>
        </div>
      </div>
      <!-- Zimobi Desktop Row -->
    </div>

    <!-- Layout for mobile devices -->
    <div class="flex flex-col gap-6 md:hidden">
      <span class="text-lg font-medium text-ash-900">
        {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.SYSTEM_NOTIFICATIONS') }}
      </span>

      <!-- Email Notifications Section -->
      <div
        class="flex flex-col gap-3 p-4 border border-solid border-ash-200 rounded-xl"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <fluent-icon
              icon="mail"
              class="flex-shrink-0 text-ash-900"
              size="18"
            />
            <span class="text-sm font-medium text-ash-900">
              {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.EMAIL') }}
            </span>
          </div>
          <CheckBox
            :is-checked="defaultNotificationsEnabled.email"
            class="!w-5 !h-5 after:!text-sm"
            value="email"
            @update="toggleDefaultNotifications('email')"
          />
        </div>
      </div>

      <!-- Push Notifications Section -->
      <div
        class="flex flex-col gap-3 p-4 border border-solid border-ash-200 rounded-xl"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <fluent-icon
              icon="alert"
              class="flex-shrink-0 text-ash-900"
              size="18"
            />
            <span class="text-sm font-medium text-ash-900">
              {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH') }}
            </span>
          </div>
          <CheckBox
            :is-checked="defaultNotificationsEnabled.push"
            class="!w-5 !h-5 after:!text-sm"
            value="push"
            @update="toggleDefaultNotifications('push')"
          />
        </div>
      </div>

      <!-- WhatsApp Notifications Section -->
      <div
        v-if="whatsappNotificationsEnabled"
        class="flex flex-col gap-3 p-4 border border-solid border-ash-200 rounded-xl"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <fluent-icon
              icon="chat"
              class="flex-shrink-0 text-ash-900"
              size="18"
            />
            <span class="text-sm font-medium text-ash-900">
              {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.WHATSAPP') }}
            </span>
          </div>
          <CheckBox
            :is-checked="defaultNotificationsEnabled.whatsapp"
            class="!w-5 !h-5 after:!text-sm"
            value="whatsapp"
            @update="toggleDefaultNotifications('whatsapp')"
          />
        </div>
      </div>
    </div>

    <div
      class="flex items-center justify-between w-full gap-2 p-4 border border-solid border-ash-200 rounded-xl"
    >
      <div class="flex flex-row items-center gap-2">
        <fluent-icon
          icon="alert"
          class="flex-shrink-0 text-ash-900"
          size="18"
        />
        <span class="text-sm text-ash-900">
          {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.BROWSER_PERMISSION') }}
        </span>
      </div>
      <FormSwitch
        :model-value="hasEnabledPushPermissions"
        @update:model-value="onTogglePushPermissions"
      />
    </div>
  </div>
</template>
